import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  TAllDemands,
  TAllTrades,
  TDemandById,
  TDemandUploadImgs,
  TPaymentHistory,
  TSelectTradeId,
  TTradePayout,
  TTradePmtLedger,
  TTraderNames,
  TTrades,
} from "../types/trades/trades";
import { TLoanFiltersData } from "../types";

type TInitialState = {
  openTrades: TAllTrades[];
  closedTrades: TAllTrades[];
  cancelTrades: TAllTrades[];
  activeTab: string;
  allTradesRefresh: boolean;
  tradeId: number;
  traderId: number;
  selectedStatus: string[];
  trades: TTrades | null;
  openTradeDetails: boolean;
  openPaymentView: boolean;
  notifications: TPaymentHistory[];
  isCollapsed: boolean;
  refresh: boolean;
  includeTestTrades: boolean;
  openDemandDetails: boolean;
  allDemandReqs: TAllDemands[];
  demandId: number;
  demandDetailsById: TDemandById | null;
  demandProposalImgs: TDemandUploadImgs[];
  selectedDemandStatus: string[];
  demandsCount: number;
  selectingTradeId: TSelectTradeId[];
  selectedTradeId: number;
  selectedDestTraderId: number;
  selectedPaymentId: number;
  moveSelection: string;
  moveComments: string;
  isPaymentDetailMove: boolean;
  isPaymentHistoryMove: boolean;
  refreshMovePayment: boolean;
  payouts: TTradePayout;
  notifiTabs: string;
  notification: boolean;
  tradeStatusFilters: {
    status: string[];
    tradeModes: string[];
    rms: TLoanFiltersData[];
    traderName: TLoanFiltersData[];
  };
  tradeStatusFiltersPanelOpenStatus: boolean;
  allTradeNames: TTraderNames[];
  tradePmtLedgerData: TTradePmtLedger[];
};

const initialState: TInitialState = {
  activeTab: "OPEN",
  allTradesRefresh: false,
  openTrades: [],
  closedTrades: [],
  tradeId: 0,
  traderId: 0,
  selectedStatus: [],
  trades: null,
  openTradeDetails: false,
  openPaymentView: false,
  notifications: [],
  isCollapsed: false,
  refresh: false,
  includeTestTrades: false,
  openDemandDetails: false,
  allDemandReqs: [],
  demandId: 0,
  demandDetailsById: null,
  demandProposalImgs: [],
  selectedDemandStatus: [],
  demandsCount: 0,
  selectingTradeId: [],
  selectedTradeId: 0,
  selectedDestTraderId: 0,
  selectedPaymentId: 0,
  moveSelection: "",
  moveComments: "",
  isPaymentDetailMove: false,
  isPaymentHistoryMove: false,
  refreshMovePayment: false,
  payouts: {
    tradesCount: 0,
    pendingRec: 0,
    pendingPayable: 0,
    trades: [],
  },
  notifiTabs: "trades",
  notification: false,
  tradeStatusFilters: {
    status: [],
    tradeModes: [],
    rms: [],
    traderName: [],
  },
  tradeStatusFiltersPanelOpenStatus: false,
  cancelTrades: [],
  allTradeNames: [],
  tradePmtLedgerData: [],
};

export const tradeDetailsSlice = createSlice({
  name: "tradeDetails",
  initialState: initialState,
  reducers: {
    resetTradeDetails: (state) => {
      return {
        ...initialState,
      };
    },

    UpdateActiveTab: (state, action: PayloadAction<string>) => {
      state.activeTab = action.payload;
    },
    setAllTradesRefresh: (state) => {
      state.allTradesRefresh = !state.allTradesRefresh;
    },

    updateAllTrades: (state, action: PayloadAction<TTrades | null>) => {
      state.trades = action.payload;
    },
    updateOpenTrades: (state, action: PayloadAction<TAllTrades[]>) => {
      state.openTrades = action.payload;
    },
    updateClosedTrades: (state, action: PayloadAction<TAllTrades[]>) => {
      state.closedTrades = action.payload;
    },
    updateCancelTrades: (state, action: PayloadAction<TAllTrades[]>) => {
      state.cancelTrades = action.payload;
    },
    setOpenTradeDetails: (state, action) => {
      state.openTradeDetails = action.payload;
    },
    updateTradeId: (
      state,
      action: PayloadAction<{
        tradeId: number;
        traderId: number;
      }>
    ) => {
      state.tradeId = action.payload.tradeId;
      state.traderId = action.payload.traderId;

      state.openPaymentView = true;
    },

    setOpenPaymentView: (state) => {
      state.openPaymentView = false;
    },
    setSelectedStatus: (state, action: PayloadAction<string[]>) => {
      state.selectedStatus = action.payload;
    },

    updateTradeNotifications: (
      state,
      action: PayloadAction<TPaymentHistory[]>
    ) => {
      state.notifications = action.payload;
    },

    setIsCollapsed(state, action: PayloadAction<boolean>) {
      state.isCollapsed = action.payload;
    },
    setPayoutRefresh: (
      state,
      action: PayloadAction<{
        refresh: boolean;
      }>
    ) => {
      state.refresh = action.payload.refresh;
    },
    setIncludeTestTrades(state, action) {
      state.includeTestTrades = action.payload;
    },
    setOpenDemandDetails: (state, action) => {
      state.openDemandDetails = action.payload;
    },
    setAllDemandReqs: (state, action: PayloadAction<TAllDemands[]>) => {
      state.allDemandReqs = action.payload;
    },
    updateDemandId: (
      state,
      action: PayloadAction<{
        demandId: number;
      }>
    ) => {
      state.demandId = action.payload.demandId;
    },

    setDemandDetailsById: (
      state,
      action: PayloadAction<{
        demandDetailsById: TDemandById | null;
      }>
    ) => {
      state.demandDetailsById = action.payload.demandDetailsById;
    },
    updateDemandProposalCropImgs: (
      state,
      action: PayloadAction<{
        demandProposalImgs: TDemandUploadImgs[];
      }>
    ) => {
      state.demandProposalImgs = action.payload.demandProposalImgs;
    },
    setSelectedDemandStatus: (state, action: PayloadAction<string[]>) => {
      state.selectedDemandStatus = action.payload;
    },
    updatedFilteredDemandLength: (
      state,
      action: PayloadAction<{
        demandsCount: number;
      }>
    ) => {
      state.demandsCount = action.payload.demandsCount;
    },
    setSelectingTradeId: (state, action: PayloadAction<TSelectTradeId[]>) => {
      state.selectingTradeId = action.payload;
    },
    setSelectTradeId: (state, action: PayloadAction<number>) => {
      state.selectedTradeId = action.payload;
    },
    setSelectedDestTraderId: (state, action: PayloadAction<number>) => {
      state.selectedDestTraderId = action.payload;
    },
    setSelectedPaymentId: (state, action: PayloadAction<number>) => {
      state.selectedPaymentId = action.payload;
    },
    setMoveSelection: (state, action: PayloadAction<string>) => {
      state.moveSelection = action.payload;
    },
    setMoveComments: (state, action: PayloadAction<string>) => {
      state.moveComments = action.payload;
    },
    resetMovePayment: (state) => {
      state.moveSelection = "";
      state.moveComments = "";
    },
    setIsPaymentDetailMove: (state, action: PayloadAction<boolean>) => {
      state.isPaymentDetailMove = action.payload;
    },
    setIsPaymentHistoryMove: (state, action: PayloadAction<boolean>) => {
      state.isPaymentHistoryMove = action.payload;
    },
    setRefreshMovePmt: (state, action: PayloadAction<boolean>) => {
      state.refreshMovePayment = action.payload;
    },
    setPayoutsData: (state, action: PayloadAction<TTradePayout>) => {
      state.payouts = action.payload;
    },
    setNotifiTabs: (state, action: PayloadAction<string>) => {
      state.notifiTabs = action.payload;
    },
    setNotification: (state, action: PayloadAction<boolean>) => {
      state.notification = action.payload;
    },
    setTradeStatusFilters: (state, action) => {
      state.tradeStatusFilters = action.payload;
      localStorage.setItem(
        "tradeStatusFilters",
        JSON.stringify(state.tradeStatusFilters)
      );
    },
    resetTradeStatusFilters: (state) => {
      state.tradeStatusFilters = initialState.tradeStatusFilters;
      localStorage.removeItem("tradeStatusFilters");
    },
    setTradeStatsFiltersPanelOpenStatus: (
      state,
      action: PayloadAction<boolean>
    ) => {
      state.tradeStatusFiltersPanelOpenStatus = action.payload;
    },
    setAllTradeNames: (state, action: PayloadAction<TTraderNames[]>) => {
      state.allTradeNames = action.payload;
    },
    setTradePmtLedgerData: (
      state,
      action: PayloadAction<TTradePmtLedger[]>
    ) => {
      state.tradePmtLedgerData = action.payload;
    },
  },
});

export const {
  UpdateActiveTab,
  setAllTradesRefresh,
  updateOpenTrades,
  updateClosedTrades,
  updateTradeId,
  setSelectedStatus,
  updateAllTrades,
  setOpenTradeDetails,
  setOpenPaymentView,
  updateTradeNotifications,
  setIsCollapsed,
  setPayoutRefresh,
  setIncludeTestTrades,
  setOpenDemandDetails,
  setAllDemandReqs,
  updateDemandId,
  setDemandDetailsById,
  updateDemandProposalCropImgs,
  setSelectedDemandStatus,
  updatedFilteredDemandLength,
  setSelectingTradeId,
  setSelectTradeId,
  setSelectedDestTraderId,
  setSelectedPaymentId,
  setMoveComments,
  setMoveSelection,
  resetMovePayment,
  setIsPaymentDetailMove,
  setIsPaymentHistoryMove,
  setRefreshMovePmt,
  setPayoutsData,
  setNotifiTabs,
  setNotification,
  setTradeStatusFilters,
  resetTradeStatusFilters,
  setTradeStatsFiltersPanelOpenStatus,
  updateCancelTrades,
  setAllTradeNames,
  setTradePmtLedgerData,
} = tradeDetailsSlice.actions;
export default tradeDetailsSlice.reducer;
